/**
 * chain-eos/mechat/send.ts — sending an encrypted DM via `mechatmechat::senddm`.
 */

import { Bytes, Checksum256, Name, PublicKey } from '@wharfkit/antelope'
import type { Session } from '../session.js'
import { MECHAT_ACTION, MECHAT_CONTRACT, MECHAT_MAX_MESSAGE_CHARS } from './constants.js'
import { encryptMessage, type EncryptedMessage } from './crypto.js'
import { resolveRecipientKey } from './keys.js'

export interface SendMessageParams {
  from: string
  to: string
  text: string
  /**
   * Recipient public key, when the caller already resolved it (contact cache).
   * Omit to look it up on-chain.
   */
  recipientPubKey?: string
}

export interface SendMessageResult {
  txid: string
  blockNum?: number
  /** Plaintext, for the local outgoing log — see the note below. */
  text: string
  to: string
  /** ms epoch, set locally at send time. */
  sentAt: number
}

/**
 * Returns an i18n key when the message is not sendable, or null when it is.
 */
export function validateMessage(params: Pick<SendMessageParams, 'from' | 'to' | 'text'>): string | null {
  const to = String(params.to ?? '').trim()
  const text = String(params.text ?? '')
  if (!to) return 'noRecipient'
  if (to.length > 13 || !/^[a-z1-5.]+$/.test(to)) return 'invalidAccount'
  if (text.trim().length === 0) return 'emptyMessage'
  if (text.length > MECHAT_MAX_MESSAGE_CHARS) return 'messageTooLong'
  return null
}

/** Characters left, for the send-box counter. Negative once over the limit. */
export function charsRemaining(text: string): number {
  return MECHAT_MAX_MESSAGE_CHARS - String(text ?? '').length
}

/**
 * Pushes an already-encrypted `senddm` envelope on-chain. Lower-level than
 * `sendMessage` — used when the ciphertext was produced elsewhere (e.g. a file
 * attachment sealed by `encryptPayCashFile`, whose `<file>…</file>` tag must be
 * encrypted under the same ephemeral key that encrypted the uploaded bytes).
 */
export async function sendEncryptedMessage(
  session: Session,
  params: { from: string; to: string; encrypted: EncryptedMessage },
): Promise<{ txid: string; blockNum?: number; to: string; sentAt: number }> {
  const to = params.to.trim()
  const { encrypted } = params

  const result = await session.transact({
    action: {
      account: MECHAT_CONTRACT,
      name: MECHAT_ACTION,
      authorization: [{ actor: params.from, permission: 'active' }],
      data: {
        from: Name.from(params.from),
        to: Name.from(to),
        iv: Bytes.from(encrypted.iv, 'hex'),
        ephem_key: PublicKey.from(encrypted.ephemPubKey),
        cipher_text: Bytes.from(encrypted.cipherText, 'hex'),
        mac: Checksum256.from(encrypted.mac),
      },
    },
  })

  const response = result.response as
    | { transaction_id?: string; processed?: { block_num?: number } }
    | undefined

  return {
    txid: response?.transaction_id ?? '',
    blockNum: response?.processed?.block_num,
    to,
    sentAt: Date.now(),
  }
}

/**
 * Encrypts `text` for `to` and pushes the `senddm` action.
 *
 * IMPORTANT — outgoing messages are not readable from the chain. The payload is
 * encrypted to the *recipient's* key and the ephemeral private key is discarded,
 * so the sender can never decrypt their own message again. The caller must persist
 * `result.text` in the device's local outgoing log if the conversation should show
 * both sides. This is exactly how the PayCash wallet behaves: sent messages live on
 * the device that sent them and nowhere else.
 */
export async function sendMessage(
  session: Session,
  params: SendMessageParams,
): Promise<SendMessageResult> {
  const invalid = validateMessage(params)
  if (invalid) throw new Error(`[mechat] ${invalid}`)

  const to = params.to.trim()
  const pubKey = params.recipientPubKey ?? (await resolveRecipientKey(to)).publicKey
  const encrypted = await encryptMessage(params.text, pubKey)

  const sent = await sendEncryptedMessage(session, { from: params.from, to, encrypted })

  return {
    txid: sent.txid,
    blockNum: sent.blockNum,
    text: params.text,
    to: sent.to,
    sentAt: sent.sentAt,
  }
}
